/**
 * Étape 6 - Signature électronique
 * Envoi du devis au client pour signature via Yousign
 * Suivi du statut de signature
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { 
    Send, FileSignature, Loader2, CheckCircle2, 
    AlertTriangle, Clock, Eye, XCircle, Mail, User, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const SIGNATURE_STATUSES = {
    non_envoye: { 
        label: 'Non envoyé', 
        color: 'bg-slate-100 text-slate-600 border-slate-200',
        icon: Clock,
        description: 'Le devis n\'a pas encore été envoyé pour signature'
    },
    envoye: { 
        label: 'Envoyé', 
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: Send,
        description: 'Le devis a été envoyé au client par email'
    },
    consulte: { 
        label: 'Consulté', 
        color: 'bg-amber-100 text-amber-700 border-amber-200',
        icon: Eye,
        description: 'Le client a ouvert le document'
    },
    signe: { 
        label: 'Signé', 
        color: 'bg-green-100 text-green-700 border-green-200',
        icon: CheckCircle2,
        description: 'Le devis a été signé par le client'
    },
    refuse: { 
        label: 'Refusé', 
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: XCircle,
        description: 'Le client a refusé de signer'
    },
    expire: { 
        label: 'Expiré', 
        color: 'bg-red-100 text-red-700 border-red-200',
        icon: Clock,
        description: 'Le délai de signature a expiré'
    }
};

// Message par défaut pour l'envoi
const DEFAULT_MESSAGE = `Bonjour,

Suite à notre échange, vous trouverez ci-joint le devis pour les travaux de rénovation énergétique de votre bien.

Ce document détaille l'ensemble des travaux prévus ainsi que les aides auxquelles vous êtes éligible.

Pour toute question, n'hésitez pas à nous contacter.

Cordialement,
L'équipe Valnexo`;

export function SignatureElectronique({ 
    prospect, 
    chantierData, 
    onUpdate, 
    onHistoryAdd,
    canSend // Audit + Subvention + Devis actif présents
}) {
    const [sending, setSending] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showSendDialog, setShowSendDialog] = useState(false);
    const [sendForm, setSendForm] = useState({
        recipient_name: prospect?.first_name + ' ' + prospect?.last_name || '',
        recipient_email: prospect?.email || '',
        message: DEFAULT_MESSAGE
    });
    
    const signatureData = chantierData?.signature || {};
    const status = signatureData.status || 'non_envoye';
    const statusInfo = SIGNATURE_STATUSES[status];
    const StatusIcon = statusInfo.icon;
    
    const isSigned = status === 'signe';
    const canResend = status === 'refuse' || status === 'expire';

    const handleSend = async () => {
        if (!sendForm.recipient_email) {
            toast.error('L\'email du destinataire est requis');
            return;
        }
        
        setSending(true);
        try {
            const response = await api.post(`/prospects/${prospect.id}/chantier/send-signature`, {
                recipient_name: sendForm.recipient_name,
                recipient_email: sendForm.recipient_email,
                message: sendForm.message
            });
            
            if (response.data.success) {
                toast.success('Devis envoyé pour signature');
                
                if (onHistoryAdd) {
                    onHistoryAdd({
                        action: 'signature_envoyee',
                        details: `Envoyé à ${sendForm.recipient_email}`,
                        timestamp: new Date().toISOString()
                    });
                }
                
                setShowSendDialog(false);
                onUpdate?.();
            } else {
                toast.error(response.data.message || 'Erreur lors de l\'envoi');
            }
        } catch (error) {
            console.error('Erreur envoi signature:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    const handleRefreshStatus = async () => {
        setRefreshing(true);
        try {
            const response = await api.get(`/prospects/${prospect.id}/chantier/signature-status`);
            
            if (response.data.status !== status && onHistoryAdd) {
                const newStatus = response.data.status;
                if (newStatus === 'consulte') {
                    onHistoryAdd({
                        action: 'signature_consultee',
                        timestamp: new Date().toISOString()
                    });
                } else if (newStatus === 'signe') {
                    onHistoryAdd({
                        action: 'signature_signee',
                        timestamp: new Date().toISOString()
                    });
                } else if (newStatus === 'refuse') {
                    onHistoryAdd({
                        action: 'signature_refusee',
                        timestamp: new Date().toISOString()
                    });
                }
            }
            
            onUpdate?.();
            toast.success('Statut mis à jour');
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setRefreshing(false);
        }
    };

    // Vérifier les conditions bloquantes
    const blockingReasons = [];
    if (!chantierData?.audit_pdf?.id) blockingReasons.push('Audit manquant');
    if (!chantierData?.subvention_pdf?.id) blockingReasons.push('Subvention manquante');
    if (!chantierData?.devis_actif_id) blockingReasons.push('Aucun devis actif sélectionné');
    
    const isBlocked = blockingReasons.length > 0;

    return (
        <>
            <Card className={cn(
                "border-2 transition-colors",
                isSigned ? "border-green-200 bg-green-50/30" : 
                status !== 'non_envoye' ? "border-blue-200 bg-blue-50/30" : 
                "border-slate-200"
            )} data-testid="etape-signature">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={cn(
                                "flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm",
                                isSigned ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                            )}>
                                6
                            </div>
                            <div>
                                <CardTitle className="text-base">Signature électronique</CardTitle>
                                <p className="text-xs text-slate-500 mt-0.5">Envoi du devis au client</p>
                            </div>
                        </div>
                        <Badge className={cn("border", statusInfo.color)}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Blocages */}
                    {isBlocked && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium text-amber-800">Conditions non remplies</p>
                                    <ul className="text-xs text-amber-700 mt-1 list-disc list-inside">
                                        {blockingReasons.map((reason, idx) => (
                                            <li key={idx}>{reason}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Statut actuel */}
                    <div className={cn(
                        "p-4 rounded-lg border mb-4",
                        statusInfo.color
                    )}>
                        <div className="flex items-center gap-3">
                            <StatusIcon className="w-8 h-8" />
                            <div>
                                <p className="font-medium">{statusInfo.label}</p>
                                <p className="text-sm opacity-80">{statusInfo.description}</p>
                                {signatureData.sent_at && (
                                    <p className="text-xs opacity-70 mt-1">
                                        Envoyé le {format(new Date(signatureData.sent_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                        {signatureData.recipient_email && ` à ${signatureData.recipient_email}`}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {(status === 'non_envoye' || canResend) && (
                            <Button
                                onClick={() => setShowSendDialog(true)}
                                disabled={isBlocked}
                                className="gap-2"
                                data-testid="btn-envoyer-signature"
                            >
                                <Send className="w-4 h-4" />
                                {canResend ? 'Renvoyer pour signature' : 'Envoyer au client pour signature'}
                            </Button>
                        )}
                        
                        {status !== 'non_envoye' && status !== 'signe' && (
                            <Button
                                variant="outline"
                                onClick={handleRefreshStatus}
                                disabled={refreshing}
                            >
                                {refreshing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Actualiser le statut
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Dialog d'envoi */}
            <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSignature className="w-5 h-5" />
                            Envoyer le devis pour signature
                        </DialogTitle>
                        <DialogDescription>
                            Le client recevra un email avec un lien pour signer le devis électroniquement.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Nom du destinataire
                            </Label>
                            <Input
                                value={sendForm.recipient_name}
                                onChange={(e) => setSendForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                                placeholder="Nom complet du client"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                Email du destinataire *
                            </Label>
                            <Input
                                type="email"
                                value={sendForm.recipient_email}
                                onChange={(e) => setSendForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                                placeholder="email@client.fr"
                                required
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Message d'accompagnement</Label>
                            <Textarea
                                value={sendForm.message}
                                onChange={(e) => setSendForm(prev => ({ ...prev, message: e.target.value }))}
                                rows={6}
                                className="resize-none"
                            />
                            <p className="text-xs text-slate-500">
                                Ce message sera inclus dans l'email envoyé au client.
                            </p>
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSend} disabled={sending || !sendForm.recipient_email}>
                            {sending ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4 mr-2" />
                                    Envoyer
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default SignatureElectronique;
